import { useEffect, useState } from "react";
import api, { formatBRL, apiErrorMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ban } from "lucide-react";
import { toast } from "sonner";

export const MOTIVOS_CANCEL_NF = [
  "Venda duplicada", "Dados do cliente errados", "Valor errado", "Venda cancelada pelo cliente", "Outro",
];

const MOTIVO_TESTID = {
  "Venda duplicada": "venda", "Dados do cliente errados": "dados", "Valor errado": "valor",
  "Venda cancelada pelo cliente": "cliente", "Outro": "outro",
};

const hoje = () => new Date().toLocaleDateString("en-CA");

function textos(modo, emitida, qtd) {
  if (modo === "pedir") {
    return ["Pedir cancelamento da NF", "O setor Fiscal recebe o pedido e faz o cancelamento. A venda continua no funil."];
  }
  if (qtd) {
    return [`Cancelar ${qtd} solicitaç${qtd === 1 ? "ão" : "ões"} de NF`,
      "Só as pendentes são canceladas. Elas saem das Pendentes e do Valor pendente; as vendas continuam no funil."];
  }
  if (emitida) {
    return ["Registrar cancelamento da NF",
      "Cancele a nota no emissor da prefeitura e registre aqui o protocolo. A venda continua no funil."];
  }
  return ["Cancelar solicitação de NF",
    "A solicitação sai das Pendentes e do Valor pendente. A venda continua no funil e dá para desfazer depois."];
}

function MotivoFields({ motivo, setMotivo, texto, setTexto }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase text-slate-600">Motivo *</Label>
      <div className="grid grid-cols-1 gap-1.5" data-testid="cancel-nf-motivos">
        {MOTIVOS_CANCEL_NF.map((m) => (
          <label key={m} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${motivo === m ? "border-red-400 bg-red-50" : "border-slate-200 hover:bg-slate-50"}`}>
            <input type="radio" name="motivo-cancel-nf" checked={motivo === m} onChange={() => setMotivo(m)} className="accent-red-600" data-testid={`cancel-nf-motivo-${MOTIVO_TESTID[m]}`} />
            {m}
          </label>
        ))}
      </div>
      {motivo === "Outro" && (
        <Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Descreva o motivo" data-testid="cancel-nf-motivo-texto" />
      )}
    </div>
  );
}

export function CancelarNfModal({ ins, ids, modo = "executar", onClose, onDone }) {
  const open = !!ins || !!ids?.length;
  const emitida = !ids?.length && ins?.nf_status === "emitida";
  const pedeProtocolo = modo === "executar" && emitida;
  const [motivo, setMotivo] = useState("");
  const [texto, setTexto] = useState("");
  const [protocolo, setProtocolo] = useState("");
  const [dataCanc, setDataCanc] = useState(hoje());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setMotivo(""); setTexto(""); setProtocolo(""); setDataCanc(hoje()); }
  }, [open]);

  const enviar = async () => {
    const body = { motivo, motivo_texto: texto.trim() || null };
    if (ids?.length) {
      const r = await api.post("/fiscal/cancelar-lote", { ids, ...body });
      return `${r.data.canceladas} solicitação(ões) cancelada(s)`;
    }
    if (modo === "pedir") {
      await api.post(`/fiscal/inscricoes/${ins.id}/pedir-cancelamento`, body);
      return "Pedido de cancelamento enviado ao Fiscal";
    }
    await api.post(`/fiscal/inscricoes/${ins.id}/cancelar`, {
      ...body, protocolo: pedeProtocolo ? protocolo.trim() : null, data_cancelamento: pedeProtocolo ? dataCanc : null,
    });
    return emitida ? "Cancelamento da NF registrado" : "Solicitação cancelada";
  };

  const confirmar = async () => {
    if (!motivo) return toast.error("Selecione o motivo do cancelamento");
    if (motivo === "Outro" && !texto.trim()) return toast.error("Descreva o motivo");
    if (pedeProtocolo && !protocolo.trim()) return toast.error("Informe o protocolo de cancelamento da NF");
    setBusy(true);
    try {
      toast.success(await enviar());
      onDone?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao cancelar"));
    } finally { setBusy(false); }
  };

  const [titulo, descricao] = textos(modo, emitida, ids?.length);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="modal-cancelar-nf">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-red-600" /> {titulo}</DialogTitle>
          <DialogDescription>{descricao}</DialogDescription>
        </DialogHeader>
        {ins && !ids?.length && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-sm" data-testid="cancel-nf-resumo">
            <b>{ins.municipio_nome}</b>{ins.municipio_uf ? ` / ${ins.municipio_uf}` : ""} · {formatBRL(ins.valor_vendido)}
            <div className="text-xs text-slate-500 truncate">{ins.curso_rotulo || ins.curso_nome}{ins.nf_numero ? ` · NF ${ins.nf_numero}` : ""}</div>
          </div>
        )}
        <MotivoFields motivo={motivo} setMotivo={setMotivo} texto={texto} setTexto={setTexto} />
        {pedeProtocolo && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-bold uppercase text-slate-600">Protocolo *</Label>
              <Input value={protocolo} onChange={(e) => setProtocolo(e.target.value)} placeholder="Nº do protocolo" data-testid="cancel-nf-protocolo" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-600">Data do cancelamento *</Label>
              <Input type="date" value={dataCanc} onChange={(e) => setDataCanc(e.target.value)} data-testid="cancel-nf-data" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="cancel-nf-voltar">Voltar</Button>
          <Button onClick={confirmar} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="cancel-nf-confirmar">
            {busy ? "Enviando..." : modo === "pedir" ? "Enviar pedido" : "Confirmar cancelamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
